import {effect, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, Observable, of} from 'rxjs';
import {shareReplay, tap} from 'rxjs/operators';
import {ITheme} from '@/themes/theme.interface';
import {ThemeService} from '@/app/core/services/theme/theme.service';
import {Theme} from '@/themes/theme';

interface CacheEntry {
  tenant: string;
  data: ITheme;
  version: string;
  expires: number;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private readonly TENANT_CONFIG_PATH = '/assets/tenants';
  private readonly DEFAULT_TENANT = 'default';
  private readonly CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly CACHE_KEY = 'app-tenant-config';

  private configCache = new Map<string, Observable<ITheme | null>>();

  public currentTenant = signal<string>(this.DEFAULT_TENANT);
  public tenantConfig = signal<ITheme | null>(null);

  constructor(private themeService: ThemeService, private http: HttpClient) {
    effect(() => {
      const config = this.tenantConfig();
      if (config) {
        this.themeService.setTheme(config);
      }
    });
  }

  detectTenant(): string {
    // Check query param for testing
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    if (tenantParam) {
      return tenantParam;
    }

    // Check local storage
    try {
      const cachedEntryStr = localStorage.getItem(this.CACHE_KEY);
      if (cachedEntryStr) {
        const cachedEntry: CacheEntry = JSON.parse(cachedEntryStr);
        if (Date.now() < cachedEntry.expires) {
          return cachedEntry.tenant;
        } else {
          localStorage.removeItem(this.CACHE_KEY);
        }
      }
    } catch (e) {
      console.error('Error reading tenant from localStorage', e);
    }

    // Check subdomain
    const hostname = window.location.hostname;
    // Special case for nom-dashboard.afirma.solutions to identify as default
    if (hostname === 'nom-dashboard.afirma.solutions') {
      return this.DEFAULT_TENANT;
    }
    const parts = hostname.split('.');
    const tenantId = parts[0];
    if (parts.length >= 3 && tenantId && tenantId !== 'www') {
      return tenantId;
    }
    return this.DEFAULT_TENANT;
  }

  getTenantConfigObservable(tenant: string): Observable<ITheme | null> {

    if (this.configCache.has(tenant)) {
      return this.configCache.get(tenant)!;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    const isDevTenant = tenantParam === tenant;

    // Skip localStorage cache if tenant is passed via URL for dev purposes
    if (!isDevTenant) {
      try {
        const cachedEntryStr = localStorage.getItem(this.CACHE_KEY);
        if (cachedEntryStr) {
          const cachedEntry: CacheEntry = JSON.parse(cachedEntryStr);

          if (Date.now() < cachedEntry.expires && cachedEntry.tenant === tenant) {
            // Fetch fresh config to check version
            this.http.get<ITheme>(`${this.TENANT_CONFIG_PATH}/${tenant}/config.json`).subscribe({
              next: (freshConfig) => {
                if (freshConfig && freshConfig.version !== cachedEntry.version) {
                  // Version mismatch, invalidate cache
                  localStorage.removeItem(this.CACHE_KEY);
                  this.configCache.delete(tenant);
                }
              },
              error: () => {
                // If fetch fails, use cache anyway
              }
            });
            const config$ = of(cachedEntry.data);
            this.configCache.set(tenant, config$);
            return config$;
          } else if (Date.now() >= cachedEntry.expires) {
            localStorage.removeItem(this.CACHE_KEY);
          }
        }
      } catch (e) {
        console.error('Error reading tenant config from localStorage', e);
        localStorage.removeItem(this.CACHE_KEY);
      }
    }

    const newConfig$ = this.http.get<ITheme>(`${this.TENANT_CONFIG_PATH}/${tenant}/config.json`).pipe(
      tap(config => {
        if (config) {
          try {
            const newEntry: CacheEntry = {
              tenant: tenant,
              data: config,
              version: config.version,
              expires: Date.now() + this.CACHE_TTL_MS
            };
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(newEntry));
          } catch (e) {
            console.error('Error saving tenant config to localStorage', e);
          }
        } else {
          console.error(`Tenant configuration for '${tenant}' not found!`);
        }
      }),
      shareReplay(1)
    );

    this.configCache.set(tenant, newConfig$);
    return newConfig$;
  }

  async loadTenantConfig(tenant: string): Promise<ITheme> {
    try {
      const theme = await firstValueFrom(this.getTenantConfigObservable(tenant));
      this.currentTenant.set(tenant);
      this.tenantConfig.set(theme);
      return theme || Theme;
    } catch (error) {
      console.warn(`Failed to load tenant ${tenant}, falling back to default`);
      this.currentTenant.set(this.DEFAULT_TENANT);
      this.tenantConfig.set(Theme);
      return Theme;
    }
  }

  getCurrentTenant(): string {
    return this.currentTenant();
  }

  getTenantConfig(): ITheme | null {
    return this.tenantConfig();
  }
}
